import { useCallback, useEffect } from "react";
import { useFocusEffect } from "expo-router";
import { v4 as uuidv4 } from "uuid";

import {
  type AppUsageDataBody,
  AppUsageType,
  type LogAppUsageRequestBody,
} from "@/schemas/api/analytics";
import { AnalyticsService } from "@/services/internal/analytics";
import { useSession } from "@/context/auth/useSession";

declare global {
  interface Window {
    dataLayer: any[]; // eslint-disable-line @typescript-eslint/no-explicit-any
    gtag: (...args: any[]) => void; // eslint-disable-line @typescript-eslint/no-explicit-any
  }
}

export function useGoogleAnalytics(): void {
  useEffect(() => {
    // Only run on web platform
    if (typeof window === "undefined") return;

    // Create script elements
    const gtagScript = document.createElement("script");
    gtagScript.async = true;
    gtagScript.src = "https://www.googletagmanager.com/gtag/js?id=G-RJXHGL8TC3";

    const inlineScript = document.createElement("script");
    inlineScript.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'G-RJXHGL8TC3');
    `;

    // Append scripts to document head
    document.head.appendChild(gtagScript);
    document.head.appendChild(inlineScript);

    // Cleanup on unmount
    return () => {
      document.head.removeChild(gtagScript);
      document.head.removeChild(inlineScript);
    };
  }, []);
}

// analytics factory functions for different event types
export const createProfileViewData = (
  targetUsername: string,
  durationInSeconds: number,
): AppUsageDataBody => ({
  type: AppUsageType.PROFILE_VIEW,
  profileViewData: {
    type: AppUsageType.PROFILE_VIEW,
    target: targetUsername,
    durationInSeconds,
  },
});

export const createImageViewData = (
  imageId: string,
  durationInSeconds: number,
): AppUsageDataBody => ({
  type: AppUsageType.IMAGE_VIEW,
  imageViewData: {
    type: AppUsageType.IMAGE_VIEW,
    imageId,
    durationInSeconds,
  },
});

/**
 * Hook to track analytics events with automatic duration tracking on screen focus/unfocus.
 *
 * This hook uses `useFocusEffect` to track when a screen is focused and automatically
 * logs an analytics event when the screen loses focus. The event data builder function
 * receives the duration in seconds, allowing for flexible event construction.
 *
 * The hook automatically retrieves `deviceId` from the session context. The `username`
 * parameter should be passed from the calling component to ensure it's always available,
 * even if the component unmounts or session state changes.
 *
 * @param username - The username of the logged-in user performing the action.
 *                   Pass `session.identity.username` from your component.
 * @param createEventBodyData - Function that builds the event-specific data object.
 *                              Receives duration in seconds as a parameter.
 *                              For non-duration events, simply ignore the duration parameter.
 *                              Use helper functions like `createProfileViewData` or `createImageViewData`
 *                              for cleaner, more maintainable code.
 * @param dependencies - Dependencies array for the useFocusEffect hook. Include any values
 *                       used in the createEventBodyData function.
 *
 * @example
 * // For PROFILE_VIEW (with duration) - using helper function
 * useTrackAnalytics(
 *   session.identity.username,
 *   (duration) => createProfileViewData(targetUsername, duration),
 *   [targetUsername]
 * );
 *
 * @example
 * // For IMAGE_VIEW (with duration) - using helper function
 * useTrackAnalytics(
 *   session.identity.username,
 *   (duration) => createImageViewData(imageId, duration),
 *   [imageId]
 * );
 *
 */
export function useTrackAnalytics(
  username: string,
  createEventBodyData: (durationInSeconds: number) => AppUsageDataBody,
  dependencies: React.DependencyList,
): void {
  const { deviceId } = useSession();
  useFocusEffect(
    useCallback(() => {
      const viewStartTime = Date.now();
      const startTimeISO = new Date(viewStartTime).toISOString();

      return () => {
        const duration = Math.round((Date.now() - viewStartTime) / 1000);

        // Initialize here in case component unmounted before service call
        const analyticsService = new AnalyticsService();

        const eventBodyData = createEventBodyData(duration);

        const body: LogAppUsageRequestBody = {
          events: [
            {
              eventId: uuidv4(),
              startTime: startTimeISO,
              deviceId,
              username,
              data: eventBodyData,
            },
          ],
        };

        analyticsService.logAppUsage(body).catch(() => {
          // Analytics error is non-critical
        });
      };
      // only rely on dependencies provided by caller
      // e.g. image view - imageId, profile view - targetUsername, etc.
    }, [...dependencies]),
  );
}
